import React from 'react';

interface VideoThumbnailProps {
  src: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders the first frame of a video as a static thumbnail.
 * - Uses IntersectionObserver to only load video when in viewport (avoids memory overload).
 * - Seeks to 0.001s on loadedmetadata + onseeked for cross-browser first-frame display.
 * - Never autoplays.
 */
const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ src, className, style }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [frameReady, setFrameReady] = React.useState(false);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let disconnected = false;

    const loadVideo = () => {
      if (disconnected) return;
      video.preload = 'metadata';
      video.src = src;
      video.load();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          loadVideo();
        }
      },
      { threshold: 0 }
    );

    observer.observe(video);

    return () => {
      disconnected = true;
      observer.disconnect();
    };
  }, [src]);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0.001;
    }
  };

  const handleSeeked = () => {
    setFrameReady(true);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: 'rgba(59,130,246,0.08)' }}>
      {/* Placeholder shown until first frame is ready */}
      {!frameReady && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4f8ef7',
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          ▶
        </div>
      )}
      <video
        ref={videoRef}
        preload="none"
        muted
        playsInline
        className={className}
        style={{
          ...style,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: frameReady ? 1 : 0,
          transition: 'opacity 0.2s',
        }}
        onLoadedMetadata={handleLoadedMetadata}
        onSeeked={handleSeeked}
      />
    </div>
  );
};

export default VideoThumbnail;
